/**
 * Centralized API Client
 * Handles all HTTP requests with error handling, loading states, and authentication
 */

import Cookies from "universal-cookie";

const cookies = new Cookies();

// Build API base URL from environment variables
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || 'https://cbt-lms.cbthost.com'}${process.env.REACT_APP_API_PREFIX || '/api'}/${process.env.REACT_APP_API_VERSION || 'v1'}`;

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = cookies.get("auth_key");
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Centralized fetch wrapper with error handling
 */
export const apiClient = async (
  endpoint,
  options = {}
) => {
  const {
    method = "GET",
    body = null,
    skipAuth = false,
    timeout = 30000,
    ...otherOptions
  } = options;

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = skipAuth ? { "Content-Type": "application/json" } : getAuthHeaders();

  const fetchOptions = {
    method,
    headers: { ...headers, ...otherOptions.headers },
    ...otherOptions,
  };

  if (body && (method === "POST" || method === "PUT" || method === "PATCH")) {
    fetchOptions.body = JSON.stringify(body);
  }

  // Add timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle response
    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");
    const data = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      const error = new Error(data?.detail || data?.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return {
      success: true,
      status: response.status,
      data,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === "AbortError") {
      return {
        success: false,
        error: "Request timeout. Please try again.",
        isTimeout: true,
      };
    }

    return {
      success: false,
      status: error.status,
      error: error.message,
      data: error.data,
    };
  }
};

/**
 * Helper methods for common HTTP methods
 */
export const api = {
  get: (endpoint, options) => apiClient(endpoint, { method: "GET", ...options }),
  post: (endpoint, body, options) => apiClient(endpoint, { method: "POST", body, ...options }),
  put: (endpoint, body, options) => apiClient(endpoint, { method: "PUT", body, ...options }),
  patch: (endpoint, body, options) => apiClient(endpoint, { method: "PATCH", body, ...options }),
  delete: (endpoint, options) => apiClient(endpoint, { method: "DELETE", ...options }),
};

/**
 * Handle file uploads
 */
export const uploadFile = async (endpoint, file, additionalData = {}) => {
  const formData = new FormData();
  formData.append("file", file);

  // Add additional form data if provided
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  const token = cookies.get("auth_key");
  const headers = {};

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout for uploads

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers,
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");
    const data = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      throw new Error(data?.detail || data?.message || `HTTP ${response.status}`);
    }

    return {
      success: true,
      status: response.status,
      data,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === "AbortError") {
      return {
        success: false,
        error: "Upload timeout. Please try again.",
        isTimeout: true,
      };
    }

    return {
      success: false,
      error: error.message,
    };
  }
};

export default apiClient;
